import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import api from '../api';
import './PackageExport.css';

// Package Export tab.
//
// Two-step flow:
//   1. "Generate refi package" button → GET /package-export/manifest
//      returns the list of every generated PDF across the other tabs.
//      The page renders that list grouped by category so the user can
//      see exactly what will be bundled.
//   2. "Download All" button → POST /package-export/generate returns
//      a ZIP blob. The ZIP is named after the pack label from the Home
//      page (e.g. "Spring Refi 2026.zip"), with four category folders
//      inside: Leases, Annotated Bank Statement, Settlement Statements,
//      Payoff Statements.
//
// Nothing is cached between visits — clicking Generate always re-scans,
// so if the user fills in a new lease or payoff on another tab and comes
// back, hitting Generate again picks up the new file.
export default function PackageExport() {
  const { id: packageId } = useParams();
  const [manifest, setManifest] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [error, setError] = useState('');

  const handleGenerate = async () => {
    setGenerating(true);
    setError('');
    try {
      const res = await api.get('/package-export/manifest', { params: { packageId } });
      setManifest(res.data);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to build package manifest.');
    } finally {
      setGenerating(false);
    }
  };

  const handleDownloadAll = async () => {
    setDownloading(true);
    setError('');
    try {
      const res = await api.post(
        '/package-export/generate',
        { packageId },
        { responseType: 'blob' }
      );
      // Try to pull the server-provided filename from Content-Disposition;
      // fall back to a client-side sanitized copy of the pack name.
      let filename = null;
      const disposition = res.headers?.['content-disposition'];
      if (disposition) {
        const match = disposition.match(/filename="([^"]+)"/);
        if (match) filename = match[1];
      }
      if (!filename) {
        const packName = manifest?.package?.name || `package-${packageId}`;
        const safe = packName.replace(/[^a-zA-Z0-9 _.\-()&]/g, '').trim();
        filename = `${safe || `package-${packageId}`}.zip`;
      }
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/zip' }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      // The server sends error JSON for failures, but we requested the
      // response as a blob, so we need to read it as text to surface the
      // message.
      let serverMsg = '';
      if (err.response?.data instanceof Blob) {
        try {
          const text = await err.response.data.text();
          serverMsg = JSON.parse(text)?.error || '';
        } catch {
          /* non-JSON blob, ignore */
        }
      } else if (err.response?.data?.error) {
        serverMsg = err.response.data.error;
      }
      setError(serverMsg || 'Failed to download ZIP.');
    } finally {
      setDownloading(false);
    }
  };

  const totalFiles = manifest
    ? (manifest.scopesOfWork?.length || 0) +
      manifest.leases.length +
      manifest.bankStatements.length +
      manifest.settlementStatements.length +
      manifest.payoffs.length
    : 0;

  return (
    <div className="package-export-page">
      <h1 className="page-title">Package Export</h1>

      {!manifest && (
        <div className="export-intro-box">
          <p>
            Click <strong>Generate refi package</strong> to scan the Scope of Work, Leases,
            Bank Statements, Settlement Statements, and Payoff Calculator tabs for every
            file saved on this refi pack. You&apos;ll see the full list before downloading.
          </p>
          <button
            type="button"
            className="export-primary-btn"
            onClick={handleGenerate}
            disabled={generating}
          >
            {generating ? 'Scanning...' : 'Generate refi package'}
          </button>
        </div>
      )}

      {error && <div className="export-error-box">{error}</div>}

      {manifest && (
        <>
          <div className="export-header-row">
            <div className="export-header-text">
              <div className="export-pack-name">
                {manifest.package?.name || `Package #${packageId}`}
              </div>
              <div className="export-total-count">
                {totalFiles} {totalFiles === 1 ? 'file' : 'files'} ready to export
              </div>
            </div>
            <div className="export-header-buttons">
              <button
                type="button"
                className="export-secondary-btn"
                onClick={handleGenerate}
                disabled={generating}
              >
                {generating ? 'Refreshing...' : 'Refresh'}
              </button>
              <button
                type="button"
                className="export-primary-btn"
                onClick={handleDownloadAll}
                disabled={downloading || totalFiles === 0}
              >
                {downloading ? 'Building ZIP...' : 'Download All'}
              </button>
            </div>
          </div>

          <ExportSection
            title="Scopes of Work"
            files={manifest.scopesOfWork || []}
            showAddress
          />
          <ExportSection title="Leases" files={manifest.leases} showAddress />
          <ExportSection
            title="Annotated Bank Statement"
            files={manifest.bankStatements}
          />
          <ExportSection
            title="Settlement Statements"
            files={manifest.settlementStatements}
            showAddress
          />
          <ExportSection
            title="Filled Payoff Statements"
            files={manifest.payoffs}
            showAddress
          />

          {totalFiles === 0 && (
            <div className="export-empty-state">
              No generated files found for this pack yet. Fill the Scope of Work, Leases,
              Bank Statements, Settlement Statements, and Payoff Calculator tabs first,
              then come back and click Refresh.
            </div>
          )}
        </>
      )}
    </div>
  );
}

function ExportSection({ title, files, showAddress }) {
  return (
    <div className="export-section">
      <div className="export-section-header">
        <h2>{title}</h2>
        <span className="export-section-count">{files.length}</span>
      </div>
      {files.length === 0 ? (
        <div className="export-subsection-empty">No files in this category.</div>
      ) : (
        <ul className="export-file-list">
          {files.map(f => (
            <li key={`${title}-${f.id}`} className="export-file-row">
              <div className="export-file-main">
                <span className="export-file-name">{f.file_name}</span>
                {showAddress && f.address && (
                  <span className="export-file-address">{f.address}</span>
                )}
              </div>
              <span className="export-file-size">{formatBytes(f.byte_size)}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function formatBytes(bytes) {
  const n = Number(bytes);
  if (!Number.isFinite(n) || n <= 0) return '';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
}
